import {
  CacheLookupPolicy,
  type IPublicClientApplication,
} from '@azure/msal-browser';
import { env } from '@sib/config';
import { LOGIN_BASE_REQUEST } from './constants';
import {
  cacheExtensionTokens,
  getCachedExtensionTokens,
} from './extension-token-cache';
import { getExtensionTokenFallback } from './extension-utils';

export interface InstanceConfig {
  clientId: string;
  tenantId: string;
  redirectUri?: string;
}

export interface GetTokensOptions {
  instance: IPublicClientApplication;
  scopes?: string[];
  forceRefresh?: boolean;
}

let isFirstTrigger = true;

export const getTokens = async ({
  instance,
  scopes,
  forceRefresh = false,
}: GetTokensOptions) => {
  try {
    const authResult = await instance.acquireTokenSilent({
      ...LOGIN_BASE_REQUEST,
      forceRefresh: isFirstTrigger || forceRefresh,
      refreshTokenExpirationOffsetSeconds: 300,
      ...(scopes && { scopes }),
      cacheLookupPolicy: CacheLookupPolicy.Default,
    });
    isFirstTrigger = false;

    const tokens = {
      idToken: authResult.idToken,
      accessToken: authResult.accessToken,
    };

    cacheExtensionTokens({ ...tokens, expiresOn: authResult.expiresOn });

    return tokens;
  } catch (error) {
    if (env.isExtension) {
      const cached = await getCachedExtensionTokens();
      if (cached) return cached;

      const authResult = await getExtensionTokenFallback();

      if (authResult) {
        const tokens = {
          idToken: authResult.idToken,
          accessToken: authResult.accessToken,
        };

        cacheExtensionTokens({ ...tokens, expiresOn: authResult.expiresOn });

        return tokens;
      }

      return null;
    }

    const activeAccount = instance.getActiveAccount();

    if (!activeAccount) return null;

    if (
      error instanceof Error &&
      error.message.includes('interaction_required')
    )
      await instance.acquireTokenRedirect({
        ...LOGIN_BASE_REQUEST,
        loginHint: activeAccount.username,
      });

    return null;
  }
};
